import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from 'react-oidc-context';
import PaymentFormService from '../../services/paymentFormService';
import Button from '../common/Button';
import './PaymentForms.css';

// SAT 4.0 Payment Methods Catalog (c_FormaPago)
const SAT_PAYMENT_METHODS = [
  { code: '01', name: 'Efectivo' },
  { code: '02', name: 'Cheque nominativo' },
  { code: '03', name: 'Transferencia electrónica de fondos' },
  { code: '04', name: 'Tarjeta de crédito' },
  { code: '05', name: 'Monedero electrónico' },
  { code: '06', name: 'Dinero electrónico' },
  { code: '08', name: 'Vales de despensa' },
  { code: '12', name: 'Dación en pago' },
  { code: '13', name: 'Pago por subrogación' },
  { code: '14', name: 'Pago por consignación' },
  { code: '15', name: 'Condonación' },
  { code: '17', name: 'Compensación' },
  { code: '23', name: 'Novación' },
  { code: '24', name: 'Confusión' },
  { code: '25', name: 'Remisión de deuda' },
  { code: '26', name: 'Prescripción o caducidad' },
  { code: '27', name: 'A satisfacción del acreedor' },
  { code: '28', name: 'Tarjeta de débito' },
  { code: '29', name: 'Tarjeta de servicios' },
  { code: '30', name: 'Aplicación de anticipos' },
  { code: '31', name: 'Intermediario pagos' },
  { code: '99', name: 'Por definir' }
];

const PaymentForms = () => {
  const auth = useAuth();
  const [paymentForms, setPaymentForms] = useState([]);
  const [filteredPaymentForms, setFilteredPaymentForms] = useState([]);
  const [searchFilter, setSearchFilter] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingPaymentForm, setEditingPaymentForm] = useState(null);
  const [deletingPaymentForm, setDeletingPaymentForm] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    formaDePago: ''
  });

  const loadPaymentForms = useCallback(async () => {
    try {
      setInitialLoading(true);
      const token = auth.user?.access_token;
      const response = await PaymentFormService.getAllPaymentForms(token);
      setPaymentForms(response.data || []);
    } catch (error) {
      setMessage({
        type: 'error',
        text: 'Error al cargar las formas de pago'
      });
    } finally {
      setInitialLoading(false);
    }
  }, [auth.user?.access_token]);

  // Load payment forms on component mount
  useEffect(() => {
    loadPaymentForms();
  }, [loadPaymentForms]);

  // Filter payment forms when search filter or payment forms change
  useEffect(() => {
    if (!searchFilter.trim()) {
      setFilteredPaymentForms(paymentForms);
    } else {
      const filtered = paymentForms.filter(paymentForm =>
        paymentForm.name.toLowerCase().includes(searchFilter.toLowerCase())
      );
      setFilteredPaymentForms(filtered);
    }
  }, [searchFilter, paymentForms]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSearchChange = (e) => {
    setSearchFilter(e.target.value);
  };

  const clearSearch = () => {
    setSearchFilter('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const token = auth.user?.access_token;
      
      if (editingPaymentForm) {
        // Update existing payment form
        await PaymentFormService.updatePaymentForm(editingPaymentForm.id, formData, token);
        setMessage({
          type: 'success',
          text: 'Forma de pago actualizada exitosamente'
        });
      } else {
        // Create new payment form
        await PaymentFormService.createPaymentForm(formData, token);
        setMessage({
          type: 'success',
          text: 'Forma de pago creada exitosamente'
        });
      }

      // Reload payment forms and close modal
      await loadPaymentForms();
      handleCloseModal();
    } catch (error) {
      setMessage({
        type: 'error',
        text: editingPaymentForm 
          ? 'Error al actualizar la forma de pago' 
          : 'Error al crear la forma de pago'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddPaymentForm = () => {
    setEditingPaymentForm(null);
    setFormData({ name: '', formaDePago: '' });
    setShowModal(true);
    setMessage({ type: '', text: '' });
  };

  const handleEditPaymentForm = (paymentForm) => {
    setEditingPaymentForm(paymentForm);
    setFormData({
      name: paymentForm.name,
      formaDePago: paymentForm.formaDePago || ''
    });
    setShowModal(true);
    setMessage({ type: '', text: '' });
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingPaymentForm(null);
    setFormData({ name: '', formaDePago: '' });
    setMessage({ type: '', text: '' });
  };

  const handleDeletePaymentForm = (paymentForm) => {
    setDeletingPaymentForm(paymentForm);
    setShowDeleteModal(true);
    setMessage({ type: '', text: '' });
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setDeletingPaymentForm(null);
    setMessage({ type: '', text: '' });
  };

  const confirmDelete = async () => {
    if (!deletingPaymentForm) return;

    setIsLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const token = auth.user?.access_token;
      await PaymentFormService.deletePaymentForm(deletingPaymentForm.id, token);
      
      setMessage({
        type: 'success',
        text: 'Forma de pago eliminada exitosamente'
      });

      // Reload payment forms and close modal
      await loadPaymentForms();
      handleCloseDeleteModal();
    } catch (error) {
      setMessage({
        type: 'error',
        text: 'Error al eliminar la forma de pago'
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="payment-forms-container">
        <div className="payment-forms-loading">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-forms-container">
      <div className="payment-forms-header">
        <div className="header-content">
          <h1>Formas de Pago</h1>
          <p>Gestiona las formas de pago disponibles en tu clínica</p>
        </div>
        <button 
          className="btn-primary add-btn"
          onClick={handleAddPaymentForm}
        >
          Agregar Forma de Pago
        </button>
      </div>

      {message.text && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}

      {/* Filters */}
      <div className="payment-forms-filters">
        <div className="search-container">
          <input
            type="text"
            placeholder="Buscar por nombre de forma de pago..."
            value={searchFilter}
            onChange={handleSearchChange}
            className="search-input"
          />
          {searchFilter && (
            <button
              className="clear-search-btn"
              onClick={clearSearch}
              title="Limpiar búsqueda"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Payment Forms Table */}
      <div className="payment-forms-table-container">
        {filteredPaymentForms.length === 0 ? (
          <div className="no-payment-forms">
            <p>No se encontraron formas de pago</p>
            {searchFilter && (
              <button className="btn-secondary" onClick={clearSearch}>
                Limpiar filtros
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <table className="payment-forms-table">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Forma de Pago SAT</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredPaymentForms.map((paymentForm) => {
                  const satMethod = SAT_PAYMENT_METHODS.find(m => m.code === paymentForm.formaDePago);
                    return (
                    <tr key={paymentForm.id}>
                      <td className="payment-form-name">
                      <div className="payment-form-details">
                        <span className="name">{paymentForm.name}</span>
                      </div>
                      </td>
                      <td className="payment-form-sat">
                      <span>
                        {satMethod ? `${satMethod.code} - ${satMethod.name}` : '-'}
                      </span>
                      </td>
                      <td className="payment-form-actions">
                      <button
                        className="btn-secondary edit-btn"
                        onClick={() => handleEditPaymentForm(paymentForm)}
                        title="Editar forma de pago"
                      >
                        Editar
                      </button>
                      <button
                        className="btn-danger delete-btn"
                        onClick={() => handleDeletePaymentForm(paymentForm)}
                        title="Eliminar forma de pago"
                      >
                        Eliminar
                      </button>
                      </td>
                    </tr>
                    );
                })}
              </tbody>
            </table>

            {/* Mobile Cards */}
            <div className="payment-forms-mobile-cards">
              {filteredPaymentForms.map((paymentForm) => {
                const satMethod = SAT_PAYMENT_METHODS.find(m => m.code === paymentForm.formaDePago);
                return (
                <div key={paymentForm.id} className="payment-form-card">
                  <div className="payment-form-card-header">
                    <div className="payment-form-card-title">{paymentForm.name}</div>
                  </div>
                  {satMethod && (
                    <div className="payment-form-card-content">
                      <div className="card-detail">
                        <strong>Forma de Pago SAT:</strong> {satMethod.code} - {satMethod.name}
                      </div>
                    </div>
                  )}
                  <div className="payment-form-card-actions">
                    <button
                      className="btn-secondary edit-btn"
                      onClick={() => handleEditPaymentForm(paymentForm)}
                      title="Editar forma de pago"
                    >
                      Editar
                    </button>
                    <button
                      className="btn-danger delete-btn"
                      onClick={() => handleDeletePaymentForm(paymentForm)}
                      title="Eliminar forma de pago"
                    >
                      Eliminar
                    </button>
                  </div>
                </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>{editingPaymentForm ? 'Editar Forma de Pago' : 'Agregar Nueva Forma de Pago'}</h3>
              <button className="modal-close" onClick={handleCloseModal}>
                ✕
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="name">
                    Nombre de la Forma de Pago <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="Ej: Efectivo, Tarjeta de Crédito, Transferencia, etc."
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="formaDePago">
                    Forma de Pago SAT (Facturación Electrónica)
                  </label>
                  <select
                    id="formaDePago"
                    name="formaDePago"
                    value={formData.formaDePago}
                    onChange={handleInputChange}
                  >
                    <option value="">Selecciona una opción</option>
                    {SAT_PAYMENT_METHODS.map((method) => (
                      <option key={method.code} value={method.code}>
                        {method.code} - {method.name}
                      </option>
                    ))}
                  </select>
                  <small className="form-help">
                    Selecciona el código SAT según el catálogo CFDI 4.0 para facturación electrónica
                  </small>
                </div>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={handleCloseModal}
                  disabled={isLoading}
                >
                  Cancelar
                </button>
                <Button
                  type="submit"
                  variant="primary"
                  loading={isLoading}
                  icon="fas fa-save"
                >
                  {isLoading
                    ? (editingPaymentForm ? 'Actualizando...' : 'Creando...')
                    : (editingPaymentForm ? 'Actualizar' : 'Crear')
                  }
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="modal delete-modal">
            <div className="modal-header">
              <h3>Confirmar Eliminación</h3>
              <button className="modal-close" onClick={handleCloseDeleteModal}>
                ✕
              </button>
            </div>
            
            <div className="modal-content">
              <p>
                ¿Estás seguro de que deseas eliminar la forma de pago{' '}
                <strong>"{deletingPaymentForm?.name}"</strong>?
              </p>
              <p className="warning-text">
                Esta acción no se puede deshacer.
              </p>
            </div>

            <div className="modal-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={handleCloseDeleteModal}
                disabled={isLoading}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn-danger"
                onClick={confirmDelete}
                disabled={isLoading}
              >
                {isLoading ? 'Eliminando...' : 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentForms;
